"use client";

import "@maptiler/sdk/dist/maptiler-sdk.css";

import { Map as MaptilerMap, NavigationControl } from "@maptiler/sdk";
import type { MapClient, MapProps, MapSettings } from "../../../types";
import { useEffect, useRef, useState } from "react";
import { MarkerComponent } from "./Marker";
import { MaptilerClient } from "../client";

const defaultMapSettings: MapSettings = {
  map: {
    zoom: 12,
    zoomable: true,
    pannable: true,
    center: {
      lng: -84.9836,
      lat: 29.7516,
    },
  },
};

export function MapComponent<T>({
  mapRef,
  token,
  data,
  settings,
  coordsGetter,
  keyGetter,
  renderMarker,
  renderPopup,
  onMapLoaded,
  onMapBoundsChanged,
  onCoordsClick,
  ...rest
}: MapProps<T, MaptilerMap>) {
  const _settings = {
    ...defaultMapSettings,
    ...settings,
  };

  const internalMapRef = useRef<MapClient<MaptilerMap> | null>(null);
  const _mapRef = mapRef ?? internalMapRef;

  const mapContapiner = useRef<HTMLDivElement | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  function emitBoundsChanged(map: MaptilerMap) {
    const bounds = map.getBounds();
    if (!bounds) {
      return;
    }

    onMapBoundsChanged?.({
      sw: {
        lat: bounds._sw.lat,
        lng: bounds._sw.lng,
      },
      ne: {
        lat: bounds._ne.lat,
        lng: bounds._ne.lng,
      },
    });
  }

  useEffect(() => {
    if (!mapContapiner.current) {
      return;
    }

    const initBounds = _settings.map?.bounds;
    const map = new MaptilerMap({
      container: mapContapiner.current,
      ...(initBounds
        ? {
            bounds: [
              [initBounds.sw.lng, initBounds.sw.lat],
              [initBounds.ne.lng, initBounds.ne.lat],
            ],
          }
        : {
            center: _settings.map?.center
              ? [_settings.map.center.lng, _settings.map.center.lat]
              : [-84.9836, 29.7516],
            zoom: _settings.map?.zoom ?? 12,
          }),
      apiKey: token,
      ...(_settings.map?.clickTolerance != null
        ? { clickTolerance: _settings.map.clickTolerance }
        : {}),
    });

    if (_settings.map?.zoomable === false) {
      map.scrollZoom.disable();
      map.doubleClickZoom.disable();
      map.boxZoom.disable();
      map.touchZoomRotate.disable();
    } else {
      map.scrollZoom.enable();
      map.doubleClickZoom.enable();
      map.boxZoom.enable();
      map.touchZoomRotate.enable();
    }

    if (_settings.map?.pannable === false) {
      map.dragPan.disable();
      map.dragRotate.disable();
      map.keyboard.disable();
    } else {
      map.dragPan.enable();
      map.dragRotate.enable();
      map.keyboard.enable();
    }

    const mapClient = new MaptilerClient(map);

    map.addControl(
      new NavigationControl({
        showZoom: _settings.map?.zoomable !== false,
        showCompass: _settings.map?.zoomable !== false,
      }),
    );

    map.on("load", () => {
      onMapLoaded?.(mapClient);
      setMapLoaded(true);
      emitBoundsChanged(map);
    });

    map.on("moveend", (e) => {
      emitBoundsChanged(e.target as MaptilerMap);
    });

    map.on("zoomend", (e) => {
      emitBoundsChanged(e.target as MaptilerMap);
    });

    map.on("click", (e) => {
      onCoordsClick?.({
        lat: e.lngLat.lat,
        lng: e.lngLat.lng,
      });
    });

    map.on("error", (e: any) => {
      console.warn("[MapTiler]", e.error?.message ?? e);
    });

    _mapRef.current = mapClient;

    return () => {
      map.remove();
      _mapRef.current = null;
    };
  }, []);

  return (
    <>
      <div ref={mapContapiner} {...rest} />
      {mapLoaded &&
        _mapRef.current &&
        data &&
        data.map((item, index) => (
          <MarkerComponent
            key={keyGetter?.(item) ?? index}
            item={item}
            index={index}
            map={_mapRef.current}
            parentProps={{
              data,
              settings,
              coordsGetter,
              keyGetter,
              renderMarker,
              renderPopup,
              token,
            }}
          />
        ))}
    </>
  );
}

MapComponent.displayName = "MapComponent";
